import { List } from 'lucide-react';
import { toMoney } from '@finvox/shared/money';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Card';
import { IconButton } from '@/components/ui/IconButton';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { cn } from '@/lib/cn';

type Props = {
  todayTreasury: string;
  todayTotalDebts: string;
  todayNetPosition: string;
  onDebtsDetailClick?: () => void;
};

export function CashbookSummaryStrip({
  todayTreasury,
  todayTotalDebts,
  todayNetPosition,
  onDebtsDetailClick
}: Props) {
  const { t } = useTranslation();
  const netNegative = toMoney(todayNetPosition).lt(0);

  const cards: Array<{
    label: string;
    value: string;
    emphasize?: boolean;
    onDetail?: () => void;
  }> = [
    { label: t('cashbook.currentBalance'), value: todayTreasury },
    {
      label: t('cashbook.debts.totalDebts'),
      value: todayTotalDebts,
      onDetail: onDebtsDetailClick
    },
    { label: t('cashbook.netPosition'), value: todayNetPosition, emphasize: true }
  ];

  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-3">
      {cards.map((c) => (
        <Card
          key={c.label}
          className={cn(
            'flex min-h-[5.5rem] flex-col p-4',
            c.emphasize && 'ring-2 ring-[var(--fv-primary)]/30'
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-[var(--fv-text-muted)] sm:text-sm">{c.label}</p>
            {c.onDetail ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={t('cashbook.debts.detailTitle')}
                onClick={c.onDetail}
                className="-me-1 -mt-1 shrink-0"
              >
                <List className="h-4 w-4" aria-hidden />
              </IconButton>
            ) : null}
          </div>
          <p
            className={cn(
              'mt-1 text-xl font-semibold tabular-nums',
              c.emphasize && netNegative && 'text-[var(--fv-danger)]'
            )}
          >
            <MoneyDisplay value={c.value} />
          </p>
        </Card>
      ))}
    </div>
  );
}
